/**
 * ProcessFlowHorizon.tsx
 *
 * W66.C — Methodology Workspace — Horizon 2: Process Flow
 *
 * Preview of the M-PACT process flow with a CTA to the full /flowcharts route.
 * FlowchartWall is not embedded inline because it requires live Supabase question
 * data and a full-height canvas context. Thumbnail+CTA used per HARD STOP rule.
 *
 * CANON: No exclamation points. No emojis. M-PACT hyphenated.
 */

import { Link } from "wouter";
import { HELIX_CHECKPOINTS } from "@/lib/helix-checkpoints";

// ─── Mini flow lane ───────────────────────────────────────────────────────────

function FlowLane() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0, position: "relative" }}>
      {/* Vertical spine */}
      <div
        style={{
          position: "absolute",
          left: 13,
          top: 14,
          bottom: 14,
          width: 2,
          background: "linear-gradient(to bottom, #DC2626, #0B0B0F)",
          borderRadius: 1,
          zIndex: 0,
        }}
      />

      {HELIX_CHECKPOINTS.map((cp, idx) => (
        <div
          key={cp.id}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "5px 0",
            position: "relative",
            zIndex: 1,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: idx < 4 ? "#DC2626" : "#0B0B0F",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              border: "2px solid #FAFAF7",
              boxShadow: "0 0 0 2px " + (idx < 4 ? "#DC2626" : "#0B0B0F"),
            }}
          >
            <span style={{ fontSize: 10, fontWeight: 800, color: "#FAFAF7" }}>
              {cp.number}
            </span>
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#0B0B0F", lineHeight: 1.2 }}>
              CP{cp.number} — {cp.name}
            </div>
            <div style={{ fontSize: 10, color: "#6B6B70" }}>
              {cp.weight}% weight
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Horizon component ────────────────────────────────────────────────────────

export default function ProcessFlowHorizon() {
  return (
    <section>
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}
        className="methodology-flow-grid"
      >
        {/* Left — mini flow preview */}
        <div
          style={{
            background: "#FAFAF7",
            border: "1px solid #E5E5E7",
            borderRadius: 10,
            padding: "20px 20px 20px 24px",
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#6B6B70",
              marginBottom: 14,
            }}
          >
            M-PACT process sequence
          </div>
          <FlowLane />
        </div>

        {/* Right — description + CTA */}
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E5E5E7",
            borderRadius: 10,
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#6B6B70",
                marginBottom: 10,
              }}
            >
              About this view
            </div>
            <h3
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#0B0B0F",
                margin: "0 0 10px",
                letterSpacing: "-0.01em",
              }}
            >
              Checkpoint Flowcharts
            </h3>
            <p style={{ fontSize: 13, color: "#3D3D42", lineHeight: 1.65, margin: "0 0 16px" }}>
              The Process Flow view renders the full M-PACT question-decision tree for each
              checkpoint — showing how answers branch into follow-up questions, how evidence
              requirements activate, and how the flow differs between a New Customer engagement
              and an Existing Fast Lane.
            </p>
            <ul style={{ margin: "0 0 20px", paddingLeft: 18, fontSize: 12, color: "#6B6B70", lineHeight: 1.7 }}>
              <li>Select any checkpoint from the top ledger bar</li>
              <li>Toggle between New Customer and Existing Customer flows</li>
              <li>View the answer trail for a specific account by appending ?accountId=...</li>
              <li>Each question node shows answer options and routing logic</li>
            </ul>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Link
              href="/flowcharts"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                background: "#0B0B0F",
                color: "#FAFAF7",
                borderRadius: 8,
                padding: "11px 20px",
                fontSize: 13,
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              Open full view — Process Flow
            </Link>
            <p style={{ fontSize: 11, color: "#9CA3AF", margin: 0 }}>
              Direct link: /flowcharts
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .methodology-flow-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
